import React, { useState, useEffect, useCallback, useRef } from 'react';
import { makeStyles, Drawer, Button as MuiButton } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import { useSelector } from 'react-redux';
import { designTokens } from '../../styles/designTokens';
import { CustomDialog, Loading, TextAlertDialog } from '@cabinet-packages/components';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/Button';
import CollectionPointsFilter from './components/CollectionPointsFilter';
import CollectionPointCard from './components/CollectionPointCard';
import useCollectionPointsData from './hooks/useCollectionPointsData';
import useCollectionPointsFilter from './hooks/useCollectionPointsFilter';

const useStyles = makeStyles({
    root: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    mobileFilterButton: {
        display: 'none',
        padding: `0 ${designTokens.spacing.lg} ${designTokens.spacing.lg}`,
        '@media (max-width: 900px)': {
            display: 'block',
        },
        '@media (max-width: 640px)': {
            padding: `0 ${designTokens.spacing.sm} ${designTokens.spacing.lg}`,
        }
    },
    filterButton: {
        ...designTokens.typography.button,
        fontSize: '15px',
        textTransform: 'none',
        backgroundColor: designTokens.colors.primary,
        color: designTokens.colors.text.white,
        padding: `${designTokens.spacing.sm} ${designTokens.spacing.lg} ${designTokens.spacing.sm}`,
        '&:hover': {
            backgroundColor: designTokens.colors.primaryDark,
        }
    },
    content: {
        width: '100%',
        maxWidth: '1232px',
        height: 'calc(100% - 109px)',
        paddingLeft: designTokens.spacing.lg,
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: '300px 1fr',
        gap: designTokens.spacing.md,
        '@media (max-width: 900px)': {
            gridTemplateColumns: '1fr',
            gap: designTokens.spacing.lg,
        },
        '@media (max-width: 640px)': {
            padding: `0 ${designTokens.spacing.sm} ${designTokens.spacing.sm}`,
        }
    },
    filterSection: {
        '@media (max-width: 900px)': {
            display: 'none',
        }
    },
    filterHeader: {
        marginBottom: designTokens.spacing.md,
    },
    geoButton: {
        width: '100%',
    },
    listSection: {
        position: 'relative',
        width: '100%',
        height: '100%',
        maxHeight: '100%',
    },
    listContainer: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        overflow: 'auto',
        scrollbarWidth: 'none',
        boxSizing: 'border-box',
        padding: `0 ${designTokens.spacing.lg} 0 ${designTokens.spacing.md}`,
        '@media (max-width: 900px)': {
            padding: `0 ${designTokens.spacing.lg} 0 0`,
        },
        '@media (max-width: 640px)': {
            padding: `0`,
        }
    },
    emptyState: {
        textAlign: 'center',
        padding: designTokens.spacing.xxxl,
        ...designTokens.typography.body,
        color: designTokens.colors.text.secondary,
    },
    drawerContent: {
        padding: designTokens.spacing.lg,
        minWidth: '300px',
    },
    applyButton: {
        marginTop: designTokens.spacing.lg,
    }
});

const CollectionPoints = () => {
    const classes = useStyles();
    const [searchValue, setSearchValue] = useState('');
    const [selectedFilters, setSelectedFilters] = useState({});
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [selectedPoint, setSelectedPoint] = useState(null);
    const [isUserSelectPoint, setIsUserSelectPoint] = useState(false);
    const selectedCardRef = useRef(null);
    const listContainerRef = useRef(null);

    const { themeMaterialUI } = useSelector(store => store.theme);
    const { punkts, clientLocation, loading } = useCollectionPointsData();

    const { filteredPoints, filterCounts } = useCollectionPointsFilter(
        punkts,
        searchValue,
        selectedFilters
    );

    const handleSearchChange = (event) => {
        setSearchValue(event.target.value);
    };

    const handleFilterChange = (filterId, isChecked) => {
        setSelectedFilters(prev => ({
            ...prev,
            [filterId]: isChecked
        }));
    };

    const handleClearAllFilters = () => {
        setSelectedFilters({});
    };

    const handlePointClick = (punkt) => {
        setSelectedPoint(punkt);
        setIsUserSelectPoint(true);
    };

    const getClosestPunkt = useCallback(() => {
        if (!clientLocation) {
            CustomDialog(TextAlertDialog, {
                text: `Будь ласка, дозвольте використання геолокації на вашому пристрої.`,
                onConfirm: () => {},
            }, false, themeMaterialUI);
            return;
        }
        if (filteredPoints && filteredPoints.length > 0) {
            setSelectedPoint(filteredPoints[0]);
            setIsUserSelectPoint(true);
        }
    }, [clientLocation, filteredPoints, themeMaterialUI]);

    // Auto-select closest point on load
    useEffect(() => {
        if (clientLocation && filteredPoints && filteredPoints.length > 0 && !isUserSelectPoint && !selectedPoint) {
            setSelectedPoint(filteredPoints[0]);
        }
    }, [clientLocation, filteredPoints, isUserSelectPoint, selectedPoint]);

    // Set first punkt if none selected
    useEffect(() => {
        if (filteredPoints && filteredPoints.length > 0 && !selectedPoint) {
            setSelectedPoint(filteredPoints[0]);
        }
    }, [filteredPoints, selectedPoint]);

    // Scroll to selected card
    useEffect(() => {
        if (selectedCardRef.current && listContainerRef.current) {
            selectedCardRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [selectedPoint]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 900 && mobileFiltersOpen) {
                setMobileFiltersOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [mobileFiltersOpen]);

    const activeFiltersCount = Object.values(selectedFilters).filter(v => v).length;

    if (loading) {
        return <Loading />;
    }

    const getPluralForm = (count) => {
        if (count === 1) return 'пункт';
        if (count >= 2 && count <= 4) return 'пункти';
        return 'пунктів';
    };

    return (
        <div className={classes.root}>
            <PageHeader
                title="Пункти забору"
                subtitle={`Знайдено ${filteredPoints.length} ${getPluralForm(filteredPoints.length)}`}
                searchInput={{
                    value: searchValue,
                    onChange: handleSearchChange,
                    placeholder: "Пошук за адресою пункту",
                    onSearch: null
                }}
            />

            <div className={classes.mobileFilterButton}>
                <MuiButton
                    className={classes.filterButton}
                    startIcon={<FilterListIcon />}
                    onClick={() => setMobileFiltersOpen(true)}
                    fullWidth
                >
                    Фільтри
                    {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
                </MuiButton>
            </div>

            <div className={classes.content}>
                <div className={classes.filterSection}>
                    <div className={classes.filterHeader}>
                        <Button
                            variant="secondary"
                            size="small"
                            icon={<MyLocationIcon />}
                            onClick={getClosestPunkt}
                            className={classes.geoButton}
                        >
                            Знайти найближчий пункт
                        </Button>
                    </div>
                    <CollectionPointsFilter
                        selectedFilters={selectedFilters}
                        onFilterChange={handleFilterChange}
                        onClearAll={handleClearAllFilters}
                        filterCounts={filterCounts}
                    />
                </div>

                <div className={classes.listSection}>
                    <div className={classes.listContainer} ref={listContainerRef}>
                        {filteredPoints.length === 0 ? (
                            <div className={classes.emptyState}>
                                {searchValue || Object.values(selectedFilters).some(v => v)
                                    ? 'Не знайдено пунктів за вибраними критеріями'
                                    : 'Немає доступних пунктів забору'}
                            </div>
                        ) : (
                            filteredPoints.map(punkt => (
                                <CollectionPointCard
                                    key={punkt.idPunkt}
                                    ref={selectedPoint && punkt.idPunkt === selectedPoint.idPunkt ? selectedCardRef : null}
                                    punkt={punkt}
                                    isSelected={selectedPoint && punkt.idPunkt === selectedPoint.idPunkt}
                                    onClick={handlePointClick}
                                />
                            ))
                        )}
                    </div>
                </div>
            </div>

            <Drawer
                anchor="bottom"
                open={mobileFiltersOpen}
                onClose={() => setMobileFiltersOpen(false)}
            >
                <div className={classes.drawerContent}>
                    <div className={classes.filterHeader}>
                        <Button
                            variant="secondary"
                            size="small"
                            icon={<MyLocationIcon />}
                            onClick={() => {
                                getClosestPunkt();
                                setMobileFiltersOpen(false);
                            }}
                            className={classes.geoButton}
                        >
                            Знайти найближчий пункт
                        </Button>
                    </div>
                    <CollectionPointsFilter
                        selectedFilters={selectedFilters}
                        onFilterChange={handleFilterChange}
                        onClearAll={handleClearAllFilters}
                        filterCounts={filterCounts}
                    />
                    <Button
                        variant="primary"
                        size="small"
                        onClick={() => setMobileFiltersOpen(false)}
                        className={classes.applyButton}
                    >
                        Застосувати
                    </Button>
                </div>
            </Drawer>
        </div>
    );
};

export default CollectionPoints;
